import { useSatuEventTracker } from 'utils/satuEvent'
import AndroidHandler from 'utils/androidHandler'
import { useAuth } from 'utils/auth'
import { APP_DEVICE_ID_STORAGE_KEY } from 'configs/android'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import { useProfile } from './useProfile'
import { useProfileInfo } from './useProfileInfo'

import type { TSETrackProfileActivities } from 'types/profile'

type TSETrackEventDefaultAtributes = {
  screen_name: string
  is_login: [boolean, 'bool']
  is_webview: [boolean, 'bool']
  email: string
  profile_user_id: string
}

type TEditProfilePageEventTrigger =
  | 'EDIT_PROFILE_PICTURE_PAGE'
  | 'EDIT_HEADER_PICTURE_PAGE'
  | 'EDIT_INFO_PAGE'

const SATU_EVENT_NAME = 'guru_core_pwa_event_tracker_v1'
const TRACKER_EVENT_NAME = 'GURU_CORE_APP'
const TRACKER_SCREEN_NAME = 'PROFILE_PAGE'
const DEFAULT_TIMEOUT = 5000 // 5 seconds

export const useSETrackEventEditProfile = () => {
  const [, , , getAppDeviceIdValue] = useLocalStorage(
    APP_DEVICE_ID_STORAGE_KEY,
    null
  )
  const { profileId } = useProfile()

  const { hasProfileInfo, profileInfoData } = useProfileInfo()

  const appDeviceID = getAppDeviceIdValue()
  const hasAppDeviceID = Boolean(appDeviceID)
  const androidHandler = AndroidHandler()
  const { session } = useAuth()
  const { SETrackEvent, SETransformAttrs } = useSatuEventTracker()

  const SETrackEventDefaultAtributes: TSETrackEventDefaultAtributes = {
    screen_name: TRACKER_SCREEN_NAME,
    is_login: [Boolean(session), 'bool'],
    is_webview: [androidHandler.enabled, 'bool'],
    email: session?.user?.email ?? null,
    profile_user_id: profileId,
  }

  const SETrackComponent = (
    activity_type: TSETrackProfileActivities,
    additionalAttributes?: Record<string, any>
  ): void => {
    SETrackEvent({
      eventName: SATU_EVENT_NAME,
      eventParams: {
        activity_type,
        event_name: TRACKER_EVENT_NAME,
        user_id: session?.user?.id,
        ...(hasAppDeviceID ? { device_id: appDeviceID } : {}),
        attributes: SETransformAttrs({
          ...SETrackEventDefaultAtributes,
          event_trigger: TRACKER_SCREEN_NAME,
          ...additionalAttributes,
        }),
      },
      timeout: DEFAULT_TIMEOUT,
      keepAlive: true,
    })
  }

  const SETrackProfileInfoItemLength = () => ({
    len_tentangSaya: [(profileInfoData?.tentangSaya ?? '').length, 'int'],
    len_kegiatanLain: [(profileInfoData?.kegiatanLain ?? '').length, 'int'],
    len_pencapaian: [(profileInfoData?.pencapaian ?? '').length, 'int'],
    len_pepatah: [(profileInfoData?.pepatah ?? '').length, 'int'],
    len_youtube: [(profileInfoData?.socialMedia?.youtube ?? '').length, 'int'],
    len_facebook: [
      (profileInfoData?.socialMedia?.facebook ?? '').length,
      'int',
    ],
    len_tiktok: [(profileInfoData?.socialMedia?.tiktok ?? '').length, 'int'],
    len_instagram: [
      (profileInfoData?.socialMedia?.instagram ?? '').length,
      'int',
    ],
    len_twitter: [(profileInfoData?.socialMedia?.twitter ?? '').length, 'int'],
  })

  type TSETrackClickProfileItemProps = {
    pageElement: 'PROFILE_PICTURE' | 'HEADER_PICTURE'
  }
  const SETrackClickProfileItem = ({
    pageElement,
  }: TSETrackClickProfileItemProps): void => {
    SETrackComponent('CLICK_PROFILE_ITEM', {
      page_element: pageElement,
    })
  }

  const SETrackClickEditProfileButton = (): void => {
    SETrackComponent('CLICK_EDIT_PROFILE_BUTTON', {
      is_profile_info_exist: [hasProfileInfo, 'bool'],
    })
  }

  const SETrackOpenEditProfilePopup = (): void => {
    SETrackComponent('OPEN_EDIT_PROFILE_POPUP')
  }

  const SETrackCloseEditProfilePopup = (): void => {
    SETrackComponent('CLOSE_EDIT_PROFILE_POPUP')
  }

  type TSETrackClickEditProfileDetailProps = {
    pageElement: 'PROFILE_PICTURE' | 'HEADER_PICTURE' | 'INFO' | 'PERSONAL_DATA'
  }
  const SETrackClickEditProfileDetail = ({
    pageElement,
  }: TSETrackClickEditProfileDetailProps): void => {
    SETrackComponent('CLICK_EDIT_PROFILE_DETAIL', {
      page_element: pageElement,
    })
  }

  type TSETrackClickBackButtonProps = {
    isUploaded: boolean
    eventTrigger: TEditProfilePageEventTrigger
  }
  const SETrackClickBackButton = ({
    isUploaded = false,
    eventTrigger,
  }: TSETrackClickBackButtonProps): void => {
    SETrackComponent('CLICK_BACK_BUTTON', {
      screen_name: eventTrigger,
      event_trigger: eventTrigger,
      is_profile_item_uploaded: [isUploaded, 'bool'],
    })
  }

  type TSETrackClickChangePictureButtonProps = {
    eventTrigger: TEditProfilePageEventTrigger
  }
  const SETrackClickChangePictureButton = ({
    eventTrigger,
  }: TSETrackClickChangePictureButtonProps): void => {
    SETrackComponent('CLICK_CHANGE_PICTURE_BUTTON', {
      screen_name: eventTrigger,
      event_trigger: eventTrigger,
    })
  }

  type TSETrackClickDeleteButtonProps = {
    eventTrigger: TEditProfilePageEventTrigger
  }
  const SETrackClickDeleteButton = ({
    eventTrigger,
  }: TSETrackClickDeleteButtonProps): void => {
    SETrackComponent('CLICK_DELETE_BUTTON', {
      screen_name: eventTrigger,
      event_trigger: eventTrigger,
    })
  }

  type TSETrackClickUploadButtonProps = {
    eventTrigger: TEditProfilePageEventTrigger
  }
  const SETrackClickUploadButton = ({
    eventTrigger,
  }: TSETrackClickUploadButtonProps): void => {
    SETrackComponent('CLICK_UPLOAD_BUTTON', {
      screen_name: eventTrigger,
      event_trigger: eventTrigger,
    })
  }

  const SETrackClickSaveButton = (): void => {
    SETrackComponent('CLICK_SAVE_BUTTON', {
      screen_name: 'EDIT_INFO_PAGE',
      event_trigger: 'EDIT_INFO_PAGE',
      ...SETrackProfileInfoItemLength(),
    })
  }

  type TSETrackClickInfoChangePopupButtonProps = {
    pageElement: 'yes' | 'no'
  }
  const SETrackClickInfoChangePopupButton = ({
    pageElement,
  }: TSETrackClickInfoChangePopupButtonProps): void => {
    SETrackComponent('CLICK_INFO_CHANGE_POPUP_BUTTON', {
      screen_name: 'EDIT_INFO_PAGE',
      event_trigger: 'EDIT_INFO_PAGE',
      page_element: pageElement,
      ...SETrackProfileInfoItemLength(),
    })
  }

  const SETrackCloseInfoChangePopup = (): void => {
    SETrackComponent('CLOSE_INFO_CHANGE_POPUP', {
      screen_name: 'EDIT_INFO_PAGE',
      event_trigger: 'EDIT_INFO_PAGE',
    })
  }

  const SETrackOpenPersonalDataPopup = (): void => {
    SETrackComponent('OPEN_PERSONAL_DATA_POPUP')
  }

  const SETrackClosePersonalDataPopup = (): void => {
    SETrackComponent('CLOSE_PERSONAL_DATA_POPUP')
  }

  return {
    // Edit Profile events
    SETrackClickProfileItem,
    SETrackClickEditProfileButton,
    SETrackOpenEditProfilePopup,
    SETrackCloseEditProfilePopup,
    SETrackClickEditProfileDetail,
    SETrackClickBackButton,
    SETrackClickChangePictureButton,
    SETrackClickDeleteButton,
    SETrackClickUploadButton,
    SETrackClickSaveButton,
    SETrackClickInfoChangePopupButton,
    SETrackCloseInfoChangePopup,
    SETrackOpenPersonalDataPopup,
    SETrackClosePersonalDataPopup,
  }
}
